import {
  type CaptionStyle,
  clipStarts,
  REEL_EDIT_LIMITS,
  type ReelTimeline,
  timelineDuration,
  type TimelineSubtitles,
  type TimelineText,
  timelineToSource,
} from '@reelplicate/flows';

/** What's picked on the timeline. */
export type Selection = { kind: 'clip' | 'text'; id: string } | null;

export type TimelineState = {
  timeline: ReelTimeline;
  /** The source reel's length: clips can't run past it. */
  sourceDuration: number;
  selected: Selection;
  /** Undo and redo stacks of earlier and later timelines. */
  past: ReelTimeline[];
  future: ReelTimeline[];
};

export type TimelineAction =
  | { type: 'reset'; timeline: ReelTimeline; sourceDuration: number }
  | { type: 'select'; selection: Selection }
  /** Remembers the timeline before a drag, which then moves without filling undo. */
  | { type: 'checkpoint' }
  | { type: 'split'; at: number }
  | {
      type: 'trim';
      id: string;
      edge: 'in' | 'out';
      /** Source seconds. */
      to: number;
      transient?: boolean;
    }
  | { type: 'move'; id: string; to: number; transient?: boolean }
  | { type: 'removeClip'; id: string }
  | { type: 'setVolume'; id: string; volume: number }
  | { type: 'addText'; at: number; style: CaptionStyle }
  | {
      type: 'updateText';
      id: string;
      patch: Partial<Omit<TimelineText, 'id'>>;
      transient?: boolean;
    }
  | { type: 'removeText'; id: string }
  | { type: 'setSubtitles'; subtitles: TimelineSubtitles | undefined }
  | { type: 'undo' }
  | { type: 'redo' };

/** How long text added at the playhead shows for. */
const NEW_TEXT_SECONDS = 2.5;
const UNDO_DEPTH = 100;
const { minClipSeconds } = REEL_EDIT_LIMITS;

const round = (seconds: number) => Math.round(seconds * 1000) / 1000;
const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

/** A short id that's new to this timeline. */
export const newId = (prefix: string) =>
  `${prefix}-${Math.random().toString(36).slice(2, 10)}`;

export const initialState = (
  timeline: ReelTimeline,
  sourceDuration: number,
): TimelineState => ({
  timeline,
  sourceDuration,
  selected: null,
  past: [],
  future: [],
});

/** Text kept inside the reel when the cuts make it shorter. */
function fitTexts(timeline: ReelTimeline): ReelTimeline {
  const duration = timelineDuration(timeline);
  const texts = timeline.texts
    .filter((text) => text.start < duration - 0.05)
    .map((text) => (text.end > duration ? { ...text, end: round(duration) } : text));
  return texts.length === timeline.texts.length &&
    texts.every((t, i) => t === timeline.texts[i])
    ? timeline
    : { ...timeline, texts };
}

/** The timeline after `change`, with undo history unless it's part of a drag. */
function edit(
  state: TimelineState,
  next: ReelTimeline,
  {
    transient = false,
    selected = state.selected,
  }: { transient?: boolean; selected?: Selection } = {},
): TimelineState {
  if (next === state.timeline) return { ...state, selected };
  const timeline = fitTexts(next);
  return {
    ...state,
    timeline,
    selected,
    past: transient ? state.past : [...state.past, state.timeline].slice(-UNDO_DEPTH),
    future: [],
  };
}

export function timelineReducer(
  state: TimelineState,
  action: TimelineAction,
): TimelineState {
  const { timeline } = state;
  switch (action.type) {
    case 'reset':
      return initialState(action.timeline, action.sourceDuration);

    case 'select':
      return { ...state, selected: action.selection };

    case 'checkpoint':
      return {
        ...state,
        past: [...state.past, timeline].slice(-UNDO_DEPTH),
        future: [],
      };

    case 'split': {
      const { index, sourceTime } = timelineToSource(timeline, action.at);
      const clip = timeline.clips[index];
      if (
        !clip ||
        sourceTime - clip.in < minClipSeconds ||
        clip.out - sourceTime < minClipSeconds
      ) {
        return state;
      }
      const second = { ...clip, id: newId('clip'), in: sourceTime };
      const clips = [...timeline.clips];
      clips.splice(index, 1, { ...clip, out: sourceTime }, second);
      return edit(
        state,
        { ...timeline, clips },
        {
          selected: { kind: 'clip', id: second.id },
        },
      );
    }

    case 'trim': {
      const clips = timeline.clips.map((clip) => {
        if (clip.id !== action.id) return clip;
        return action.edge === 'in'
          ? { ...clip, in: round(clamp(action.to, 0, clip.out - minClipSeconds)) }
          : {
              ...clip,
              out: round(
                clamp(action.to, clip.in + minClipSeconds, state.sourceDuration),
              ),
            };
      });
      return edit(state, { ...timeline, clips }, { transient: action.transient });
    }

    case 'move': {
      const from = timeline.clips.findIndex((clip) => clip.id === action.id);
      const to = clamp(action.to, 0, timeline.clips.length - 1);
      if (from === -1 || from === to) return state;
      const clips = [...timeline.clips];
      const [clip] = clips.splice(from, 1);
      clips.splice(to, 0, clip!);
      return edit(state, { ...timeline, clips }, { transient: action.transient });
    }

    case 'removeClip': {
      if (timeline.clips.length <= 1) return state;
      const clips = timeline.clips.filter((clip) => clip.id !== action.id);
      if (clips.length === timeline.clips.length) return state;
      return edit(state, { ...timeline, clips }, { selected: null });
    }

    case 'setVolume': {
      const clips = timeline.clips.map((clip) =>
        clip.id === action.id ? { ...clip, volume: clamp(action.volume, 0, 2) } : clip,
      );
      return edit(state, { ...timeline, clips });
    }

    case 'addText': {
      if (timeline.texts.length >= REEL_EDIT_LIMITS.maxTexts) return state;
      const duration = timelineDuration(timeline);
      const start = round(clamp(action.at, 0, Math.max(duration - 0.5, 0)));
      const text: TimelineText = {
        id: newId('text'),
        text: 'Your text',
        start,
        end: round(Math.min(start + NEW_TEXT_SECONDS, duration)),
        position: 'top',
        style: action.style,
      };
      return edit(
        state,
        { ...timeline, texts: [...timeline.texts, text] },
        { selected: { kind: 'text', id: text.id } },
      );
    }

    case 'updateText': {
      const duration = timelineDuration(timeline);
      const texts = timeline.texts.map((text) => {
        if (text.id !== action.id) return text;
        const next = { ...text, ...action.patch };
        // Keep it on the reel and at least a beat long.
        const start = round(clamp(next.start, 0, duration - 0.2));
        const end = round(clamp(next.end, start + 0.2, duration));
        return { ...next, start, end };
      });
      return edit(state, { ...timeline, texts }, { transient: action.transient });
    }

    case 'removeText':
      return edit(
        state,
        { ...timeline, texts: timeline.texts.filter((text) => text.id !== action.id) },
        { selected: null },
      );

    case 'setSubtitles':
      return edit(state, { ...timeline, subtitles: action.subtitles });

    case 'undo': {
      const previous = state.past.at(-1);
      if (!previous) return state;
      return {
        ...state,
        timeline: previous,
        past: state.past.slice(0, -1),
        future: [timeline, ...state.future],
        selected: keepSelection(previous, state.selected),
      };
    }

    case 'redo': {
      const [next, ...rest] = state.future;
      if (!next) return state;
      return {
        ...state,
        timeline: next,
        past: [...state.past, timeline],
        future: rest,
        selected: keepSelection(next, state.selected),
      };
    }
  }
}

/** The selection, if it still exists in `timeline`. */
function keepSelection(timeline: ReelTimeline, selected: Selection): Selection {
  if (!selected) return null;
  const list = selected.kind === 'clip' ? timeline.clips : timeline.texts;
  return list.some((item) => item.id === selected.id) ? selected : null;
}

/** Each clip with where it sits on the edited timeline. */
export const placedClips = (timeline: ReelTimeline) => {
  const starts = clipStarts(timeline);
  return timeline.clips.map((clip, i) => ({
    clip,
    index: i,
    start: starts[i] ?? 0,
    length: round(clip.out - clip.in),
  }));
};

/** "0:07.4" */
export function formatTime(seconds: number): string {
  const safe = Math.max(0, seconds);
  const minutes = Math.floor(safe / 60);
  const rest = safe - minutes * 60;
  return `${minutes}:${rest.toFixed(1).padStart(4, '0')}`;
}
